/* eslint-disable no-console */
import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';
import getErrorMessage from '../utils/error';
import { IPaginationData, IParamsSearch, IPartner, IResponse } from '../types/types';
import { deletePartner, getAllPartner, createPartner, updatePartner, getPartnerById } from '../utils/api';

interface IUsePartner {
  loading: boolean;
  partner: IPartner | null;
  partners: IPartner[] | null;
  paginationData: IPaginationData | null;
  abortController: AbortController | null;
  cancelFetchPartner: () => void;
  fetchPartner: (params?: IParamsSearch) => Promise<IResponse>;
  deletePartner: (id: string) => Promise<IResponse>;
  createPartner: (data: IPartner) => Promise<IResponse>;
  updatePartner: (id: string, data: IPartner) => Promise<IResponse>;
  getPartner: (id: string) => Promise<IResponse>;
}

const usePartner: UseBoundStore<StoreApi<IUsePartner>> = create((set, get) => ({
  loading: false,
  partner: null,
  partners: null,
  paginationData: null,
  abortController: null,
  cancelFetchPartner: () => {
    const { abortController } = get();
    if (abortController && !abortController.signal.aborted) {
      abortController.abort();
      set({ loading: false, abortController: null });
    }
  },
  fetchPartner: async (params = {}) => {
    const newAbortController = new AbortController();

    try {
      set(() => ({ loading: true }));

      if (get().abortController) {
        get().abortController?.abort();
      }

      set(() => ({ abortController: newAbortController }));

      // Construct query parameters
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

      // Add filters
      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value) queryParams.append(`${key}`, value.toString());
        });
      }

      // Add sorting
      if (params.sorting) {
        queryParams.append('sortBy', params.sorting.field);
        queryParams.append('sortOrder', params.sorting.order);
      }

      const { success, message, data } = await getAllPartner(queryParams.toString(), newAbortController.signal);

      // Only update state if this request wasn't aborted
      if (!newAbortController.signal.aborted) {
        if (!success) {
          throw new Error(message);
        }

        set(() => ({
          partners: data.data,
          paginationData: data.pagination,
        }));
      }

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Request dibatalkan');
        return {
          success: false,
          message: 'Request dibatalkan',
        };
      }

      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      const currentController = get().abortController;
      if (currentController === newAbortController && !currentController?.signal.aborted) {
        set(() => ({ loading: false }));
      }
    }
  },
  deletePartner: async (id) => {
    const createToast = toast.loading('Delete partner...');
    try {
      set(() => ({ loading: true }));

      const prevPartners = get().partners;

      const { success, message } = await deletePartner(id);

      if (!success) {
        throw new Error(message);
      }

      set({ partners: prevPartners?.filter((partner) => partner.partner_id !== id) });

      toast.update(createToast, {
        position: 'top-right',
        render: 'Delete partner successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      // Rollback jika error terjadi
      set({ partners: get().partners });

      toast.update(createToast, {
        position: 'top-right',
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  createPartner: async (data) => {
    const createToast = toast.loading('Create partner...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await createPartner(data);

      if (!success) {
        throw new Error(message);
      }

      set((state) => ({
        partners: [...(state.partners || []), data], // Tambah data ke state partners
      }));

      toast.update(createToast, {
        position: 'top-right',
        render: 'Create partner successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      // Rollback: Hapus kembali data yang ditambahkan sebelumnya
      set((state) => ({
        partners: state.partners?.filter((partner) => partner !== data),
      }));

      toast.update(createToast, {
        position: 'top-right',
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  updatePartner: async (id, data) => {
    // Simpan data partner lama untuk rollback
    const oldPartnerData = get().partners?.find((partner) => partner.partner_id === id);
    const createToast = toast.loading('Update partner...');

    try {
      set(() => ({ loading: true }));
      const { success, message } = await updatePartner(id, data);

      if (!success) {
        throw new Error(message);
      }

      // Update state dengan data baru
      set((state) => ({
        partners: state.partners?.map((partner) =>
          partner.partner_id === id
            ? { ...partner, ...data } // Gabungkan data lama dengan update
            : partner,
        ),
      }));

      toast.update(createToast, {
        position: 'top-right',
        render: 'Update partner successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      // Rollback: Kembalikan ke data partner lama
      if (oldPartnerData) {
        set((state) => ({
          partners: state.partners?.map((partner) => (partner.partner_id === id ? oldPartnerData : partner)),
        }));
      }

      toast.update(createToast, {
        position: 'top-right',
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  getPartner: async (id) => {
    try {
      set(() => ({ loading: true }));

      const { success, message, data } = await getPartnerById(id);

      if (!success) {
        throw new Error(message);
      }

      set({ partner: data });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      toast.success(errorMessage, {
        position: 'top-right',
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
}));

// Subscribe untuk mengecek perubahan state
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// const unsubscribe = usePartner.subscribe((state) => {
//   console.log('State partner:', state);
// });

export default usePartner;
